import { SUPPORTED_BROWSERS } from '@bulknutrients/bulk-shared-lib/constants';
import {
  getBrowserName,
  getBrowserVersion,
  isSupportedBrowser,
} from '@bulknutrients/bulk-shared-lib/utils';
import { Container } from '@components/Container';
import { StandardLayout } from '@components/Layouts/StandardLayout';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import { TextLink } from '@components/Typography/TextLink/TextLink';
import Head from 'next/head';
import Image from 'next/legacy/image';
import { type ReactElement, useEffect, useState } from 'react';

export default function BrowserSupportPage(): ReactElement {
  const [browserName, setBrowserName] = useState('');
  const [browserVersion, setBrowserVersion] = useState('');
  const [isSupported, setIsSupported] = useState(true);

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      const userAgent = navigator.userAgent;
      setBrowserName(getBrowserName(userAgent));
      setBrowserVersion(getBrowserVersion(userAgent, browserName));
      setIsSupported(isSupportedBrowser());
    }
    return function cleanup() {
      mounted = false;
    };
  }, [browserName]);

  return (
    <>
      <Head>
        <title>Bulk Nutrients - Supported Browsers</title>
        <meta name="title" content="Bulk Nutrients - Supported Browsers" />
        <meta
          name="description"
          content="Bulk Nutrients | Australia’s first choice for nutritional supplements"
        />
        <meta name="robots" content="noindex" />
      </Head>
      {/** biome-ignore lint/correctness/useUniqueElementIds: we just the main-content with the skip to content button */}
      <Container
        as="main"
        id="main-content"
        className="grid-cols-[1fr_min(var(--width-narrow-page),calc(100%-16px))_1fr]"
      >
        <section className="m-auto max-w-[500px]">
          <h1>Supported Browsers</h1>
          <Paragraph>
            We've detected that you're currently using{' '}
            <b>
              {browserName} {browserVersion}
            </b>
            , which <em>{isSupported ? 'is' : 'is not'}</em> supported by us.
          </Paragraph>
          <Paragraph>
            For the best experience browsing and ordering quality supplements,
            we suggest using one of the following...
          </Paragraph>
          <br />
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
            {SUPPORTED_BROWSERS.map((b) => (
              <div key={b.name} className="flex flex-col items-center gap-2.5">
                <Image
                  src={b.image}
                  width={80}
                  height={80}
                  alt={`${b.name} icon`}
                  layout="fixed"
                />
                <TextLink target="_blank" href={b.download}>
                  {b.name}
                </TextLink>
              </div>
            ))}
          </div>
          <br />
          <Paragraph>
            As always, reach out if you{' '}
            <TextLink href="/contact-us">need the help</TextLink> of our
            friendly Customer Service team.
          </Paragraph>
        </section>
      </Container>
    </>
  );
}

BrowserSupportPage.getLayout = function getLayout(page: ReactElement) {
  return <StandardLayout>{page}</StandardLayout>;
};
